import { locale } from "../services/LocaleService.js";

export const IntroPage = async () => {
    // [I18n] Force update meta tags specific to Intro
    locale.updateMeta({
        title: 'seo.default_title', // or specific intro title
        description: 'intro.desc_1'
    });

    const container = document.createElement('div');
    container.className = "min-h-screen bg-[#050505] text-white overflow-x-hidden font-sans selection:bg-fuchsia-500 selection:text-white relative";

    // 1. CSS
    const style = document.createElement('style');
    style.textContent = `
        @keyframes float-cloud {
            0% { transform: translateY(0) translateX(0); opacity: 0; }
            10% { opacity: 0.8; }
            90% { opacity: 0.8; }
            100% { transform: translateY(-100px) translateX(20px); opacity: 0; }
        }
        @keyframes scan-line {
            0% { top: -10%; opacity: 0; }
            50% { top: 110%; opacity: 1; }
            100% { top: 110%; opacity: 0; }
        }
        @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
            animation: fade-in-up 1s ease-out forwards;
        }
        @keyframes fade-in-up-strong {
            from { opacity: 0; transform: translateY(40px); filter: blur(10px); }
            to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        .animate-fade-in-up-strong {
            animation: fade-in-up-strong 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .lang-tag {
            position: absolute;
            color: rgba(255,255,255,0.05);
            font-weight: 900;
            user-select: none;
            pointer-events: none;
            animation: float-cloud 20s infinite linear;
        }
        .hero-gradient-text {
            background: linear-gradient(to right, #4ade80, #3b82f6);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            transition: all 1s ease;
        }
        .hero-gradient-text.mode-2 {
            background: linear-gradient(to right, #e879f9, #818cf8);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        /* Slider Styles */
        .comparison-slider {
            position: relative;
            width: 100%;
            height: 100%;
            overflow: hidden;
            border-radius: 1rem;
            cursor: ew-resize;
        }
        .comparison-item {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        .comparison-handle {
            position: absolute;
            top: 0;
            bottom: 0;
            width: 4px;
            background: white;
            z-index: 10;
            pointer-events: none;
            box-shadow: 0 0 10px rgba(0,0,0,0.5);
        }
        .comparison-handle::after {
            content: '< >';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: white;
            color: black;
            padding: 4px 8px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: bold;
            white-space: nowrap;
        }
        /* Terminal Styles */
        .terminal-window {
            background: #0c0c0c;
            border: 1px solid #333;
            box-shadow: 0 20px 50px rgba(0,0,0,0.5);
            font-family: 'Courier New', Courier, monospace;
        }
        .typing-cursor::after {
            content: '▋';
            animation: blink 1s infinite;
        }
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
    `;
    container.appendChild(style);

    // 2. JSON-LD Injection (SoftwareApplication)
    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Ssulwha Engine",
        "applicationCategory": "MultimediaApplication",
        "operatingSystem": "Web",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "description": "The First Non-Destructive AI Localization Engine for Webtoons.",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "ratingCount": "120"
        },
        "sameAs": ["https://www.producthunt.com/posts/ssulwha-engine"] // Placeholder
    });
    document.head.appendChild(jsonLd);

    // 3. Background Effect (Floating Tags)
    const langContainer = document.createElement('div');
    langContainer.className = "fixed inset-0 z-0 overflow-hidden pointer-events-none";
    const langs = ["KR", "EN", "JP", "CN", "ES", "FR", "DE", "RU", "TH", "VN"];
    for (let i = 0; i < 20; i++) {
        const span = document.createElement('span');
        span.className = "lang-tag text-4xl md:text-8xl opacity-10";
        span.innerText = langs[Math.floor(Math.random() * langs.length)];
        span.style.left = Math.random() * 100 + '%';
        span.style.top = Math.random() * 100 + '%';
        span.style.animationDuration = (20 + Math.random() * 20) + 's';
        span.style.animationDelay = (Math.random() * -20) + 's';
        langContainer.appendChild(span);
    }
    container.appendChild(langContainer);


    // ==========================================
    // SECTION 1: HERO (The Engine)
    // ==========================================
    const heroSection = document.createElement('section');
    heroSection.className = "relative z-10 min-h-screen flex flex-col items-center justify-center p-6 md:p-12";
    heroSection.innerHTML = `
        <div class="absolute top-6 left-6 md:left-12 flex items-center gap-2 animate-fade-in-up">
            <span class="w-8 h-8 rounded-full bg-gradient-to-tr from-fuchsia-600 to-indigo-600 flex items-center justify-center font-bold text-white text-lg shadow-lg">S</span>
            <span class="font-bold text-xl tracking-tight">SSULWHA ENGINE</span>
        </div>

        <div class="flex flex-col md:flex-row items-center justify-center w-full max-w-7xl gap-12 md:gap-24">
            <!-- Left: Copy -->
            <div class="flex-1 text-center md:text-left z-20 max-w-2xl">
                <!-- SLOGAN INSERTION (Moved Top + Spacing + Mobile Fix) -->
                <div class="mt-32 md:mt-0 mb-24 animate-fade-in-up-strong">
                    <span class="text-xl md:text-3xl font-serif italic tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-400 drop-shadow-[0_0_15px_rgba(168,85,247,0.5)]">
                        "${locale.t('intro.slogan')}"
                    </span>
                </div>

                <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6 animate-fade-in-up" style="animation-delay: 0.2s;">
                    <span class="w-2 h-2 rounded-full bg-green-400 animate-pulse" id="hero-dot"></span>
                    <span class="text-xs font-bold text-green-400 tracking-wider uppercase transition-all duration-500" id="hero-status">${locale.t('intro.status_active')}</span>
                </div>

                <h1 class="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-8 leading-[1] animate-fade-in-up transition-all duration-1000">
                    ${locale.t('intro.hero_title')}
                    <br/>
                    <span class="text-3xl md:text-5xl lg:text-6xl font-thin text-gray-500 block my-2">
                        ${locale.t('intro.hero_subtitle')}
                    </span>
                    <span class="hero-gradient-text" id="hero-dynamic-word"></span>
                </h1>

                <!-- Dynamic Typewriter Description -->
                <div class="h-24 mb-10 w-full flex items-center justify-center md:justify-start">
                    <p class="text-lg md:text-xl text-gray-300 leading-relaxed font-mono" id="hero-typewriter">
                        > Initializing Engine...
                    </p>
                </div>

                <div class="flex flex-col sm:flex-row gap-4 justify-center md:justify-start animate-fade-in-up">
                    <button onclick="window.router.navigate('/')" class="px-8 py-4 bg-white text-black font-black text-lg rounded-full hover:bg-gray-200 transition-all shadow-xl shadow-white/10 flex items-center justify-center gap-2 group transform hover:scale-105 active:scale-95">
                        ${locale.t('intro.cta_start')}
                        <span class="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
                    </button>
                </div>

                <!-- Product Hunt Badge -->
                <div class="mt-8 animate-fade-in-up flex justify-center md:justify-start" style="animation-delay: 0.1s;">
                    <a href="https://www.producthunt.com/products/ssulwha-world?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-ssulwha-world" target="_blank" rel="noopener noreferrer" class="block transition-transform hover:scale-105">
                        <img src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1080696&theme=light&t=1771312302605" alt="Ssulwha World - AI-powered Webtoon: Zero-Click Localization & Creator Studio | Product Hunt" style="width: 250px; height: 54px;" width="250" height="54" />
                    </a>
                </div>


            </div>

            <!-- Right: Phone Mockup (Autoplay) -->
            <div class="flex-1 w-full max-w-md relative z-10 animate-fade-in-up" style="animation-delay: 0.2s;">
                 <div class="phone-mockup bg-black rounded-[3rem] overflow-hidden relative aspect-[9/19] border-4 border-gray-800 ring-1 ring-white/20 shadow-2xl skew-y-0 md:rotate-y-12 transform transition-transform hover:scale-[1.02] duration-500 group">
                    <div class="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-black rounded-b-2xl z-50"></div>
                            <!-- MODE 1: BABEL FISH (Translation) -->
                            <div class="absolute inset-0 transition-opacity duration-1000" id="screen-mode-1">
                                <img src="/login.webp" class="absolute inset-0 w-full h-full object-cover opacity-60 grayscale-[50%]">
                                <div class="absolute inset-0 bg-black/30"></div>
                                
                                <!-- Bubbles -->
                                <div class="absolute top-1/3 left-4 p-3 bg-white text-black rounded-xl rounded-tl-none max-w-[80%] text-sm font-bold shadow-lg">
                                    <span class="animate-pulse">${locale.t('intro.phone_analyzing')}</span>
                                </div>
                                <div class="absolute top-1/2 right-4 p-3 bg-green-500 text-black rounded-xl rounded-tr-none max-w-[80%] text-sm font-bold shadow-lg mt-12">
                                     <span class="text-xs block opacity-70 mb-1">${locale.t('intro.phone_detected')}: Korean</span>
                                     "${locale.t('intro.phone_text_1')}"
                                </div>

                                <!-- Scanner -->
                                <div class="absolute w-full h-1 bg-green-400 blur-md shadow-[0_0_20px_rgba(74,222,128,0.8)] animate-[scan-line_2s_ease-in-out_infinite]"></div>
                            </div>

                            <!-- MODE 2: CINE-NOVEL (Immersion) -->
                            <div class="absolute inset-0 transition-opacity duration-1000 opacity-0" id="screen-mode-2">
                                <img src="/login.webp" class="absolute inset-0 w-full h-full object-cover opacity-80 scale-110 transition-transform duration-[10s]">
                                <div class="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                                
                                <div class="absolute bottom-10 left-6 right-6 text-gray-100">
                                    <h3 class="text-fuchsia-400 text-xs font-bold tracking-widest mb-2 border-b border-fuchsia-500/30 pb-2 inline-block">${locale.t('intro.phone_cine_ep')}</h3>
                                    <p class="font-serif leading-relaxed text-sm drop-shadow-md">
                                        ${locale.t('intro.phone_cine_text_1')} <br><br>
                                        ${locale.t('intro.phone_cine_text_2')}
                                    </p>
                                </div>
                            </div>
                </div>
            </div>
        </div>
    `;
    container.appendChild(heroSection);


    // ==========================================
    // SECTION 2: COMPARISON (Interactive Slider)
    // ==========================================
    const compareSection = document.createElement('section');
    compareSection.className = "relative z-10 py-24 px-4 bg-black/50 border-y border-white/5";
    compareSection.innerHTML = `
        <div class="max-w-6xl mx-auto">
            <div class="text-center mb-16 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
                <h2 class="text-3xl md:text-5xl font-black mb-4">${locale.t('intro.compare_title')}</h2>
                <p class="text-gray-400 text-lg">${locale.t('intro.compare_desc')}</p>
            </div>

            <div class="relative w-full aspect-video md:aspect-[21/9] max-h-[600px] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000 delay-200">
                
                <!-- Bottom Image (Standard / Right Side) -->
                <div class="absolute inset-0">
                    <img src="/login.webp" class="w-full h-full object-cover">
                    <!-- Clean Text Overlay (Moved to Right) -->
                    <div class="absolute bottom-4 right-4 md:bottom-10 md:right-10 p-4 md:p-6 bg-black/60 backdrop-blur-md rounded-xl text-white max-w-[90%] md:max-w-lg border-r-2 md:border-r-4 border-green-400 text-right">
                        <h3 class="text-sm md:text-xl font-bold text-green-400 mb-1 md:mb-2">${locale.t('intro.slider_new_title')}</h3>
                        <p class="text-xs md:text-lg leading-relaxed">${locale.t('intro.slider_new_desc_1')}<br>${locale.t('intro.slider_new_desc_2')}</p>
                    </div>
                </div>

                <!-- Top Image (Old / Left Side - Clipped) -->
                <div class="absolute inset-0 w-[50%] overflow-hidden border-r-2 md:border-r-4 border-white shadow-[0_0_30px_rgba(0,0,0,0.5)]" id="slider-overlay">
                    <img src="/login.webp" class="absolute top-0 left-0 w-[200%] h-full object-cover max-w-none grayscale"> <!-- width 200% to counter clip -->
                    <!-- Ugly White Box Overlay -->
                    <div class="absolute bottom-4 left-4 md:bottom-10 md:left-10 p-4 md:p-6 bg-white text-black max-w-[90%] md:max-w-lg shadow-xl">
                        <h3 class="text-sm md:text-xl font-bold text-red-600 mb-1 md:mb-2">${locale.t('intro.slider_old_title')}</h3>
                        <p class="text-xs md:text-lg leading-relaxed bg-black text-white p-1 inline-block">${locale.t('intro.slider_old_desc')}</p>
                    </div>
                </div>

                <!-- Handle -->
                <div class="absolute top-0 bottom-0 left-[50%] w-10 -ml-5 cursor-ew-resize flex items-center justify-center z-30 group" id="slider-handle">
                    <div class="w-1 h-full bg-white/50 group-hover:bg-white transition-colors"></div>
                    <div class="absolute w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-black font-bold">
                        <span class="material-symbols-outlined text-sm">code</span>
                    </div>
                </div>

                <!-- Labels -->
                <div class="absolute top-4 left-4 bg-black/50 px-3 py-1 rounded text-xs font-bold text-gray-300 pointer-events-none z-20">${locale.t('intro.compare_old')}</div>
                <div class="absolute top-4 right-4 bg-black/50 px-3 py-1 rounded text-xs font-bold text-green-400 pointer-events-none z-20">${locale.t('intro.compare_new')}</div>
            </div>
        </div>
    `;
    container.appendChild(compareSection);


    // ==========================================
    // SECTION 3: CINE-NOVEL (Parallax)
    // ==========================================
    const cineSection = document.createElement('section');
    cineSection.className = "relative z-10 py-32 px-4 overflow-hidden";
    cineSection.innerHTML = `
        <div class="max-w-4xl mx-auto text-center relative z-20">
            <div class="inline-block px-4 py-2 rounded-full border border-fuchsia-500/50 text-fuchsia-400 font-bold mb-6 text-sm tracking-widest uppercase animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
                ${locale.t('intro.status_cine')}
            </div>
            <h2 class="text-4xl md:text-6xl font-black mb-8 leading-tight animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000 delay-100">
                ${locale.t('intro.format_title')}
            </h2>
            
            <div class="relative aspect-[9/16] md:aspect-[16/9] w-full bg-black rounded-3xl overflow-hidden shadow-2xl border border-white/10 group animate-on-scroll opacity-0 scale-90 transition-all duration-1000 delay-200">
                <!-- Background is parallaxed via JS -->
                <img src="/login.webp" class="absolute inset-0 w-full h-[120%] object-cover opacity-60 transition-transform duration-[10s] ease-linear group-hover:scale-110" id="cine-bg">
                <div class="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                
                <div class="absolute bottom-0 left-0 right-0 p-8 md:p-16 text-left">
                    <p class="text-xl md:text-3xl font-serif leading-relaxed text-gray-200 drop-shadow-lg" id="cine-text">
                        "${locale.t('intro.format_desc')}"
                    </p>
                </div>
            </div>
        </div>
    `;
    container.appendChild(cineSection);


    // ==========================================
    // SECTION 4: GEO TERMINAL (Data Porn)
    // ==========================================
    const geoSection = document.createElement('section');
    geoSection.className = "relative z-10 py-24 px-4 bg-[#0a0a0a] border-t border-white/5";
    geoSection.innerHTML = `
        <div class="max-w-5xl mx-auto flex flex-col md:flex-row gap-12 items-center">
            
            <div class="flex-1 space-y-8 animate-on-scroll opacity-0 translate-x-[-50px] transition-all duration-1000">
                <h2 class="text-3xl md:text-5xl font-black text-white">
                    ${locale.t('intro.geo_title')}
                </h2>
                <div class="h-1 w-20 bg-green-500"></div>
                <p class="text-xl text-gray-400 leading-relaxed">
                    "${locale.t('intro.geo_desc')}"
                </p>
                <ul class="space-y-4 text-gray-400 font-mono text-sm">
                    <li class="flex items-center gap-3">
                        <span class="material-symbols-outlined text-green-500">check_circle</span> Automated Metadata Injection
                    </li>
                    <li class="flex items-center gap-3">
                        <span class="material-symbols-outlined text-green-500">check_circle</span> JSON-LD for GoogleBot
                    </li>
                    <li class="flex items-center gap-3">
                        <span class="material-symbols-outlined text-green-500">check_circle</span> Zero-Cost Organic Growth
                    </li>
                </ul>
            </div>

            <div class="flex-1 w-full animate-on-scroll opacity-0 translate-x-[50px] transition-all duration-1000 delay-200">
                <div class="terminal-window rounded-xl overflow-hidden text-xs md:text-sm text-green-400 p-4 h-80 relative">
                    <div class="absolute top-0 left-0 right-0 h-8 bg-[#1a1a1a] flex items-center px-4 gap-2 border-b border-[#333]">
                        <div class="w-3 h-3 rounded-full bg-red-500"></div>
                        <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <div class="w-3 h-3 rounded-full bg-green-500"></div>
                        <span class="ml-2 text-gray-500 font-sans text-xs">bot_activity.log — bash</span>
                    </div>
                    <div class="mt-8 space-y-2 h-full overflow-hidden" id="terminal-content">
                        <!-- Logs injected by JS -->
                    </div>
                </div>
            </div>
        </div>
    `;
    container.appendChild(geoSection);

    // Footer
    const footer = document.createElement('footer');
    footer.className = "text-center py-10 text-gray-600 text-xs relative z-10";
    footer.innerHTML = `&copy; 2026 Ssulwha World. ${locale.t('intro.footer_copyright')}`;
    container.appendChild(footer);


    // ==========================================
    // LOGIC & ANIMATIONS
    // ==========================================
    requestAnimationFrame(() => {
        // 1. Hero Typewriter & Translation Logic
        const typeDest = container.querySelector('#hero-typewriter');
        const heroDynamicWord = container.querySelector('#hero-dynamic-word');
        const phoneBubble = container.querySelector('#phone-bubble');
        const texts = [
            { t: "Analyzing Structure...", l: "EN", w: "Webtoons" },
            { t: "구조 분석 중...", l: "KR", w: "웹툰" },
            { t: "構造分析中...", l: "JP", w: "漫画" },
            { t: "Analizando...", l: "ES", w: "Webtoons" }
        ];
        let textIdx = 0;

        const updateHero = () => {
            const current = texts[textIdx];
            // Simulate Typewriter
            typeDest.innerHTML = `<span class="text-green-400">root@ssulwha:~$</span> ${current.t}<span class="typing-cursor"></span>`;

            // Update Dynamic Word
            heroDynamicWord.innerText = current.w;

            // Update Phone Bubble
            if (phoneBubble) {
                phoneBubble.innerHTML = `
                    <span class="text-xs block opacity-50 mb-1">${locale.t('intro.phone_detected')}: ${current.l}</span>
                    "${current.t}"
                `;
            }

            textIdx = (textIdx + 1) % texts.length;
        };
        setInterval(updateHero, 3000);
        updateHero(); // Init


        // 2. Comparison Slider Logic
        const sliderContainer = container.querySelector('.comparison-slider');
        const sliderOverlay = container.querySelector('#slider-overlay');
        const sliderHandle = container.querySelector('#slider-handle');
        const sliderRoot = sliderOverlay?.parentElement;

        if (sliderRoot) {
            let isDragging = false;

            const updateSlider = (x) => {
                const rect = sliderRoot.getBoundingClientRect();
                let percentage = ((x - rect.left) / rect.width) * 100;
                percentage = Math.max(0, Math.min(100, percentage));

                sliderOverlay.style.width = percentage + '%';
                sliderHandle.style.left = percentage + '%';
            };

            sliderRoot.addEventListener('mousedown', () => isDragging = true);
            window.addEventListener('mouseup', () => isDragging = false);
            window.addEventListener('mousemove', (e) => {
                if (!isDragging) return;
                updateSlider(e.clientX);
            });
            // Touch support
            sliderRoot.addEventListener('touchstart', () => isDragging = true);
            window.addEventListener('touchend', () => isDragging = false);
            window.addEventListener('touchmove', (e) => {
                if (!isDragging) return;
                updateSlider(e.touches[0].clientX);
            });
        }


        // 3. Scroll Animations (Fade In)
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.remove('opacity-0', 'translate-y-10', 'translate-x-[50px]', 'translate-x-[-50px]', 'scale-90');
                }
            });
        }, { threshold: 0.2 });

        container.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));


        // 4. Terminal Logs
        const termContent = container.querySelector('#terminal-content');
        const logs = [
            locale.t('intro.term_log_1'),
            locale.t('intro.term_log_2'),
            "> Generating JSON-LD...",
            locale.t('intro.term_log_3'),
            "> Ping GoogleBot (Indexing)... 200 OK",
            "> Ping BingBot (Indexing)... 200 OK",
            locale.t('intro.term_log_4')
        ];

        const addLog = async () => {
            for (const log of logs) {
                const p = document.createElement('div');
                p.className = "text-green-500 font-mono text-sm border-l-2 border-green-500 pl-2 animate-pulse";
                p.innerText = log;
                termContent.appendChild(p);
                termContent.scrollTop = termContent.scrollHeight;
                await new Promise(r => setTimeout(r, 800)); // Delay
                p.classList.remove('animate-pulse');
                p.classList.add('opacity-70');
            }
            // Loop?
            setTimeout(() => {
                termContent.innerHTML = '';
                addLog();
            }, 3000);
        };

        // Trigger generic logs if user scrolls to terminal
        const termObserver = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && termContent.children.length === 0) {
                addLog();
            }
        });
        if (termContent) termObserver.observe(termContent.parentElement);

    });

    return container;
};
